import { useEffect } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Instagram, Youtube, Twitter } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/releases", label: "Releases" },
  { to: "/about", label: "About" },
];

export default function PublicLayout() {
  const { user } = useAuth();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#F7F3EA]/80 border-b border-purple-100/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src="/logo.jpg" alt="Mixed By Empress" className="w-11 h-11 rounded-full object-cover ring-1 ring-[#D4AF37]/50 shadow-md" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-purple-700 font-semibold" : "text-slate-500 hover:text-purple-700"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                data-testid="nav-portal-link"
                className="text-sm text-slate-600 hover:text-purple-700 transition-colors"
              >
                {user.role === "admin" ? "Admin Studio" : "My Portal"}
              </Link>
            ) : (
              <Link to="/auth" data-testid="nav-login" className="text-sm text-slate-600 hover:text-purple-700 transition-colors">
                Client Login
              </Link>
            )}
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="text-sm px-5 py-2.5 rounded-full bg-obsidian text-white border border-obsidian hover:border-gold hover:text-gold transition-colors"
            >
              Book
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <Outlet />
      </main>

      <footer className="bg-obsidian text-white grain relative" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 grid gap-12 md:grid-cols-3">
          <div>
            <img src="/logo.jpg" alt="Mixed By Empress" className="w-20 h-20 rounded-full object-cover ring-1 ring-[#D4AF37]/50 shadow-xl" />
            <p className="text-sm text-white/50 mt-4 leading-relaxed max-w-xs">
              Your favorite engineer. A luxury digital studio for recording and mixing — every session confirmed personally by Empress.
            </p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-300 mb-4">Contact</p>
            <a href="mailto:MixedByEmpress@gmail.com" className="block text-sm text-white/70 hover:text-gold transition-colors" data-testid="footer-email">
              MixedByEmpress@gmail.com
            </a>
            <p className="text-sm text-white/70 mt-2">Nashville · Atlanta · Remote</p>
            <div className="flex gap-3 mt-5">
              {[Instagram, Youtube, Twitter].map((Icon, i) => (
                <a key={i} href="#" aria-label="Social link" data-testid={`footer-social-${i}`}
                   className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-colors">
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-300 mb-4">Studio</p>
            <div className="flex flex-col gap-2 text-sm text-white/70">
              <Link to="/book" className="hover:text-gold transition-colors" data-testid="footer-book">Book a session</Link>
              <Link to="/auth" className="hover:text-gold transition-colors" data-testid="footer-client-login">Client login</Link>
              <Link to="/policies" className="hover:text-gold transition-colors" data-testid="footer-policies">Studio policies</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs font-mono text-white/40 tracking-widest">
          © 2026 MIXED BY EMPRESS · MIXEDBYEMPRESS.COM
        </div>
      </footer>
    </div>
  );
}
